import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon, PlusIcon, MinusIcon, ClockIcon, InfoIcon, FlameIcon, UndoIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Dish, money } from '../../data/food';

/**
 * Ordering a dish, the way people actually order one: pick the size, see what
 * is already in it and take out what you don't want, then add what you do.
 * The total moves as you go, so nothing is a surprise at the end.
 *
 * Rendered through a portal by the caller — on desktop the photo holds a fixed
 * left column while only the choices scroll, so the sheet never becomes a long
 * tunnel of options with the food lost off the top.
 */
export function DishConfigurator({
  title,
  kitchen,
  image,
  images,
  dish,
  onClose,
  onAdd
}: {
  title: string;
  kitchen: string;
  image: string;
  /** Every photo the kitchen has, so the shopper can look before ordering. */
  images?: string[];
  dish: Dish;
  onClose: () => void;
  /** Unit price keeps the quantity editable later, in the cart. */
  onAdd: (summary: string, unitPrice: number, qty: number) => void;
}) {
  const shots = images && images.length > 0 ? images : [image];
  const [frame, setFrame] = useState(0);
  const step = (dir: 1 | -1) => setFrame(f => (f + dir + shots.length) % shots.length);
  const [sizeId, setSizeId] = useState(dish.sizes.find(s => s.note)?.id ?? dish.sizes[0].id);
  /** Ingredients the shopper has taken out. */
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');

  // Escape closes, and the page behind must not scroll while this is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  const size = dish.sizes.find(s => s.id === sizeId) ?? dish.sizes[0];
  const extrasTotal = dish.extras.filter(x => added.has(x.id)).reduce((sum, x) => sum + x.price, 0);
  const unitPrice = size.price + extrasTotal;
  const total = unitPrice * qty;

  /** Extras keep their menu grouping, in the order the kitchen wrote them. */
  const extraGroups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, typeof dish.extras>();
    dish.extras.forEach(x => {
      if (!map.has(x.group)) {
        map.set(x.group, []);
        order.push(x.group);
      }
      map.get(x.group)!.push(x);
    });
    return order.map(g => ({
      name: g,
      items: map.get(g)!
    }));
  }, [dish.extras]);
  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };
  const removedItems = dish.included.filter(i => removed.has(i.id));
  const addedItems = dish.extras.filter(x => added.has(x.id));
  const submit = () => {
    const parts = [size.label];
    if (removedItems.length) parts.push(`no ${removedItems.map(i => i.label.toLowerCase()).join(', no ')}`);
    if (addedItems.length) parts.push(`+ ${addedItems.map(x => x.label).join(', ')}`);
    if (note.trim()) parts.push(`“${note.trim()}”`);
    onAdd(parts.join(' · '), unitPrice, qty);
  };
  return <div role="dialog" aria-modal="true" aria-label={`Order ${title}`} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-2128" data-mp="14nfb271dmvfi">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2129" data-mp="byrl3y5onhvm" />
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full sm:max-w-3xl bg-white rounded-t-3xl sm:rounded-3xl shadow-large overflow-hidden flex flex-col sm:flex-row max-h-[92vh] sm:max-h-[86vh] sm:h-[680px]" data-id="element-2130" data-mp="gx1eq4breaf4">
        {/* The food, at its natural proportions. A full-height column forced a
            wide food photo into a tall portrait crop, so the image keeps a
            fixed aspect and the name sits on white underneath it. */}
        <div className="flex-shrink-0 sm:w-[40%] sm:border-r border-slate-100 flex flex-col bg-white" data-id="element-2131" data-mp="3bacy5rkg7jjy">
          <div className="relative aspect-[16/10] sm:aspect-[4/3] bg-slate-100 group" data-id="element-2132" data-mp="36de6rgs65qpg">
            <img src={shots[frame]} alt={title} className="w-full h-full object-cover" data-id="element-2133" data-mp="1gwlhw97m61fj" />
            {shots.length > 1 && <>
                <button onClick={() => step(-1)} aria-label="Previous photo" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-slate-700 shadow-sm sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-2134" data-mp="3nldpx81yiho2">
                  <ChevronLeftIcon className="w-4 h-4" data-id="element-2135" data-mp="34uwb87dylmlp" />
                </button>
                <button onClick={() => step(1)} aria-label="Next photo" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-slate-700 shadow-sm sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-2136" data-mp="39tz64z086cfy">
                  <ChevronRightIcon className="w-4 h-4" data-id="element-2137" data-mp="y4prnmkyxhnw" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-2138" data-mp="y2lox7yt2op7">
                  {shots.map((_, i) => <button key={i} onClick={() => setFrame(i)} aria-label={`Photo ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-200 ease-out ${i === frame ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`} data-id="element-2139" data-mp="2221y50h7td7e" data-mpi={i} />)}
                </div>
              </>}
          </div>

          <div className="px-5 py-4" data-id="element-2140" data-mp="2lqaor6341b4s">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 truncate" data-id="element-2141" data-mp="2lrc9y3dube08">
              {kitchen}
            </p>
            <h2 className="font-display font-bold text-lg text-dark-navy leading-snug mt-1" data-id="element-2142" data-mp="2c0zfnjg846gw">
              {title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[12px] font-semibold text-slate-500" data-id="element-2143" data-mp="39gwwsxd46ves">
              <span className="inline-flex items-center gap-1.5" data-id="element-2144" data-mp="34o3ra79tnj1n">
                <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2145" data-mp="3c1l56oyjo6ec" />
                {dish.prepTime}
              </span>
              {dish.spiceLevel && <span className="inline-flex items-center gap-1.5" data-id="element-2146" data-mp="3h1h78inolnr9">
                  <FlameIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2147" data-mp="3lx642e8bgqjv" />
                  {dish.spiceLevel}
                </span>}
              {dish.calories && <span data-id="element-2148" data-mp="3v076o4xz3zis">{dish.calories}</span>}
            </div>
          </div>
        </div>

        {/* Only the choices scroll, under a solid bar so nothing slides
            beneath a floating close button. */}
        <div className="flex-1 flex flex-col min-w-0" data-id="element-2149" data-mp="8kvvwv7kuikz">
          <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 border-b border-slate-100 flex-shrink-0" data-id="element-2150" data-mp="dixr2eia28ct">
            <p className="text-[13px] font-bold text-dark-navy" data-id="element-2151" data-mp="hl11czt7n6wi">
              Customise your order
            </p>
            <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-dark-navy hover:bg-slate-100 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2152" data-mp="mknfrckryt6w">
              <XIcon className="w-4 h-4" data-id="element-2153" data-mp="1ht078m71b44d" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-6" data-id="element-2154" data-mp="1ctx08w7huntr">
            {/* 1 — size */}
            <section data-id="element-2155" data-mp="p3jhxxepo3cd">
              <div className="flex items-baseline justify-between gap-3 mb-3" data-id="element-2156" data-mp="p5487atzt04f">
                <h3 className="text-sm font-bold text-dark-navy" data-id="element-2157" data-mp="k5hvd2i2tlw5">
                  Choose a size
                </h3>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-2158" data-mp="yuxj09mb9b7m">
                  Required
                </span>
              </div>
              <div className="space-y-2" data-id="element-2159" data-mp="6dbipqe4tx1g">
                {dish.sizes.map(s => <button key={s.id} onClick={() => setSizeId(s.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors duration-150 ease-out ${sizeId === s.id ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2160" data-mp="1ei41un3zzzp">
                    <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sizeId === s.id ? 'border-primary-indigo' : 'border-slate-300'}`} data-id="element-2161" data-mp="231tb6xyywtrn">
                      {sizeId === s.id && <span className="w-2 h-2 rounded-full bg-primary-indigo" data-id="element-2162" data-mp="30qwg22uldmvm" />}
                    </span>
                    <span className="text-sm font-semibold text-dark-navy flex-1 truncate" data-id="element-2163" data-mp="2qt8il55lq19r">
                      {s.label}
                    </span>
                    {s.note && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-bold text-slate-500 flex-shrink-0" data-id="element-2164" data-mp="1bekuz5dnfrnb">
                        {s.note}
                      </span>}
                    <span className="text-sm font-bold text-dark-navy flex-shrink-0" data-id="element-2165" data-mp="2f46ix9gcxuey">
                      {money(s.price)}
                    </span>
                  </button>)}
              </div>
            </section>

            {/* 2 — what's already in it, and what you can take out */}
            <section data-id="element-2166" data-mp="1revdvfrz5dv0">
              <div className="flex items-baseline justify-between gap-3 mb-1" data-id="element-2167" data-mp="1rec5oqs4m3u6">
                <h3 className="text-sm font-bold text-dark-navy" data-id="element-2168" data-mp="1wdf43sdlpbt6">
                  What's included
                </h3>
                {removed.size > 0 && <button onClick={() => setRemoved(new Set())} className="inline-flex items-center gap-1.5 text-[12px] font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-2169" data-mp="1ifqj8kjja4o4">
                    <UndoIcon className="w-3.5 h-3.5" data-id="element-2170" data-mp="3gs8ofmqojy4k" />
                    Put everything back
                  </button>}
              </div>
              <p className="text-[12px] text-slate-500 mb-3" data-id="element-2171" data-mp="114uy8tus24vs">
                Tap anything you'd rather leave out.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-2172" data-mp="1ex1cubia83t4">
                {dish.included.map(i => {
                const out = removed.has(i.id);
                return <button key={i.id} disabled={!i.removable} onClick={() => setRemoved(s => toggle(s, i.id))} title={i.removable ? out ? `Add ${i.label} back` : `Remove ${i.label}` : 'This one stays — it is part of the dish'} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors duration-150 ease-out ${out ? 'border-slate-200 bg-slate-50 text-slate-400 line-through' : i.removable ? 'border-slate-200 bg-white text-slate-700 hover:border-error/40 hover:text-error' : 'border-slate-100 bg-slate-50/70 text-slate-400 cursor-default'}`} data-id="element-2173" data-mp="1otnxaz6p4sum">
                      {out ? <PlusIcon className="w-3.5 h-3.5" data-id="element-2174" data-mp="2hgr4r4fxy04x" /> : i.removable ? <MinusIcon className="w-3.5 h-3.5" data-id="element-2175" data-mp="2mfllm0ucleme" /> : <CheckIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-2176" data-mp="2rari1tldmboq" />}
                      <span className="font-medium" data-id="element-2177" data-mp="2w9uc6iztixhx">{i.label}</span>
                    </button>;
              })}
              </div>
              {removedItems.length > 0 && <p className="mt-3 px-3.5 py-2.5 bg-slate-50 rounded-xl text-[12px] font-semibold text-slate-600" data-id="element-2178" data-mp="358pli3y5snpg">
                  The kitchen will make it without{' '}
                  {removedItems.map(i => i.label.toLowerCase()).join(', ')}.
                </p>}
            </section>

            {/* 3 — extras, grouped the way the menu groups them */}
            {extraGroups.map(group => <section key={group.name} data-id="element-2179" data-mp="fnnvw7tpgys6">
                <div className="flex items-baseline justify-between gap-3 mb-3" data-id="element-2180" data-mp="1caiu0n5ss5bn">
                  <h3 className="text-sm font-bold text-dark-navy" data-id="element-2181" data-mp="189gv67fslva7">
                    {group.name}
                  </h3>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-2182" data-mp="13hz4cc7hhb2y">
                    Optional
                  </span>
                </div>
                <div className="space-y-2" data-id="element-2183" data-mp="1w032txlvxdjo">
                  {group.items.map(x => {
                const on = added.has(x.id);
                return <button key={x.id} onClick={() => setAdded(s => toggle(s, x.id))} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors duration-150 ease-out ${on ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2184" data-mp="1r703f8uff9vk">
                        <span className={`w-[18px] h-[18px] rounded-md border flex-shrink-0 flex items-center justify-center ${on ? 'bg-primary-indigo border-primary-indigo' : 'border-slate-300'}`} data-id="element-2185" data-mp="1uhb8t4buodsa">
                          {on && <CheckIcon className="w-3 h-3 text-white" data-id="element-2186" data-mp="1kqzzt3db7eej" />}
                        </span>
                        <span className="text-sm font-semibold text-dark-navy flex-1 truncate" data-id="element-2187" data-mp="1pp1gjhn9j1mu">
                          {x.label}
                        </span>
                        <span className="text-sm font-bold text-slate-500 flex-shrink-0" data-id="element-2188" data-mp="2i75gz8l85e67">
                          + {money(x.price)}
                        </span>
                      </button>;
              })}
                </div>
              </section>)}

            {/* 4 — anything the options didn't cover */}
            <section data-id="element-2189" data-mp="31dmpelizst5c">
              <h3 className="text-sm font-bold text-dark-navy mb-2" data-id="element-2190" data-mp="2wcpb4k13sj94">
                Anything else?
              </h3>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. cut in half, sauce on the side" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-2191" data-mp="28m33gtv0h0zn" />
            </section>

            {dish.allergens.length > 0 && <div className="flex items-start gap-2.5 px-4 py-3 bg-slate-50 rounded-2xl" data-id="element-2192" data-mp="3p0uy7dfmqkc4">
                <InfoIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-2193" data-mp="vvcaw0afccq" />
                <p className="text-[12px] text-slate-500 leading-relaxed" data-id="element-2194" data-mp="4wowpfeiu90v">
                  Contains {dish.allergens.join(', ').toLowerCase()}. Prepared
                  in a kitchen that handles other allergens.
                </p>
              </div>}
          </div>

          {/* The total is always in view, and it moves as you choose */}
          <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-t border-slate-100 bg-white flex-shrink-0" data-id="element-2195" data-mp="3icmwtt524vcd">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 flex-shrink-0" data-id="element-2196" data-mp="3nbppsf7rjb3z">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty === 1} aria-label="One fewer" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white disabled:opacity-30 transition-colors duration-150 ease-out" data-id="element-2197" data-mp="3s2ze9chks8ea">
                <MinusIcon className="w-4 h-4" data-id="element-2198" data-mp="2s0t87l9ryq1z" />
              </button>
              <span className="w-6 text-center text-sm font-bold text-dark-navy" data-id="element-2199" data-mp="2n544d5qq2mzv">
                {qty}
              </span>
              <button onClick={() => setQty(q => Math.min(20, q + 1))} aria-label="One more" className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:bg-white transition-colors duration-150 ease-out" data-id="element-2200" data-mp="2i6sw2zg2skjr">
                <PlusIcon className="w-4 h-4" data-id="element-2201" data-mp="24c0abj9le84q" />
              </button>
            </div>
            <button onClick={submit} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-2202" data-mp="1zeq9z8ko0c24">
              Add to order
              <span className="w-1 h-1 rounded-full bg-white/40" data-id="element-2203" data-mp="1lfqpmd46qjbr" />
              {money(total)}
            </button>
          </div>
        </div>
      </motion.div>
    </div>;
}